import { useEffect, useState } from "react";
import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import TaxPayer from "../../models/tax-payer";
import TaxPayerWhatsappSettings from "../../models/tax-payer-whatsapp-settings";
import Errors, { getFieldErrors } from "../../models/errors";
import { createItem, getItem } from "../../api/generics";
import { NOT_FOUND, SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, successAlert, warningAlert } from "../utils/messages";
import Loading from "../indicators/Loading";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";

const defaults = (): TaxPayerWhatsappSettings => ({
  apiUrl: "https://graph.facebook.com/v25.0",
  businessAccountId: "",
  phoneNumberId: "",
  accessToken: "",
  sendEnable: false,
  sendToClient: false,
  additionalRecipients: [],
  templates: [],
});
const labels: Record<string, string> = {
  invoice: "Factura",
  credit_note: "Nota de crédito",
  debit_note: "Nota de débito",
  remission_note: "Nota de remisión",
  receipt: "Recibo",
  cancellation: "Cancelación",
  disablement: "Inutilización",
};
const statuses: Record<string, string> = {
  APPROVED: "Aprobada",
  PENDING: "En revisión por Meta",
  REJECTED: "Rechazada por Meta",
  PAUSED: "Pausada",
  DISABLED: "Deshabilitada",
  ERROR: "Error de sincronización",
  INCOMPATIBLE: "Contenido incompatible",
  NOT_CREATED: "Sin registrar",
};

const TaxPayerWhatsappSettingsForm: React.FC<{
  taxPayer: TaxPayer;
}> = ({ taxPayer }) => {
  const [settings, setSettings] = useState(defaults);
  const [recipients, setRecipients] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    getItem<TaxPayerWhatsappSettings>(
      `/tax_payers/${taxPayer.id}/whatsapp_settings/`,
    )
      .then((result) => {
        if (!active) return;
        if (result.status === SUCCESS && result.data) {
          setSettings({ ...result.data, accessToken: "" });
          setRecipients(result.data.additionalRecipients.join(", "));
          setSaved(true);
        } else if (result.status === NOT_FOUND) {
          setSettings(defaults());
          setRecipients("");
          setSaved(false);
        } else {
          setLoadError(
            result.detail || "No se pudo cargar la configuración de WhatsApp.",
          );
        }
        setDirty(false);
      })
      .catch(() => {
        if (active)
          setLoadError("No se pudo cargar la configuración de WhatsApp.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [taxPayer.id]);

  const change = (
    field: keyof TaxPayerWhatsappSettings,
    value: string | boolean,
  ) => {
    setSettings((previous) => ({ ...previous, [field]: value }));
    setDirty(true);
  };

  const submit = async (syncOnly: boolean) => {
    if (submitting) return;
    setSubmitting(true);
    setErrors({});
    try {
      const result = await createItem<
        TaxPayerWhatsappSettings,
        Partial<TaxPayerWhatsappSettings>
      >(
        `/tax_payers/${taxPayer.id}/whatsapp_settings/${syncOnly ? "sync" : "update"}/`,
        syncOnly
          ? {}
          : {
              apiUrl: settings.apiUrl,
              businessAccountId: settings.businessAccountId,
              phoneNumberId: settings.phoneNumberId,
              accessToken: settings.accessToken,
              sendEnable: settings.sendEnable,
              sendToClient: settings.sendToClient,
              additionalRecipients: recipients
                .split(",")
                .map((value) => value.trim())
                .filter(Boolean),
            },
      );
      if (result.status !== SUCCESS || !result.data) {
        setErrors(result.errors || {});
        errorAlert(
          result.detail || "No se pudo guardar la configuración de WhatsApp.",
        );
        return;
      }
      setSettings({ ...result.data, accessToken: "" });
      setRecipients(result.data.additionalRecipients.join(", "));
      setSaved(true);
      setDirty(false);
      if (
        result.data.templates.some(
          (template) =>
            template.error ||
            ["REJECTED", "DISABLED", "PAUSED"].includes(template.status),
        )
      ) {
        warningAlert(
          "Configuración guardada. Revise las plantillas que requieren atención.",
        );
      } else {
        successAlert(
          "Configuración guardada y plantillas sincronizadas. Meta debe aprobarlas antes del envío.",
        );
      }
    } catch {
      errorAlert(
        "No se pudo completar la solicitud. Vuelva a cargar la configuración antes de reintentar.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading height="30vh" />;
  if (loadError)
    return (
      <>
        <p role="alert" className="text-danger">
          {loadError}
        </p>
      </>
    );

  const fields: {
    key: "apiUrl" | "businessAccountId" | "phoneNumberId" | "accessToken";
    label: string;
    help: string;
  }[] = [
    {
      key: "apiUrl",
      label: "URL de la API",
      help: "URL de Graph API de Meta con versión, por ejemplo https://graph.facebook.com/v25.0.",
    },
    {
      key: "businessAccountId",
      label: "ID de cuenta de WhatsApp (WABA)",
      help: "Identificador de la cuenta empresarial; se usa para registrar las plantillas.",
    },
    {
      key: "phoneNumberId",
      label: "ID del número de teléfono",
      help: "Identificador de Meta del número remitente, no el número de teléfono.",
    },
    {
      key: "accessToken",
      label: "Token de acceso",
      help: settings.hasAccessToken
        ? "Token guardado. Deje vacío para conservarlo; ingrese uno nuevo para reemplazarlo."
        : "Token de usuario del sistema con permisos whatsapp_business_management y whatsapp_business_messaging.",
    },
  ];

  return (
    <fieldset disabled={submitting}>
      <p>
        Al guardar se registran automáticamente las siete plantillas en español.
        Las plantillas existentes se reutilizan. Meta revisa y aprueba cada
        plantilla.
      </p>
      {fields.map(({ key, label, help }) => (
        <CFormGroup key={key}>
          <CRow>
            <CCol md={4}>
              <CLabel htmlFor={`whatsapp-${key}`}>{label}</CLabel>
            </CCol>
            <CCol>
              <CInput
                id={`whatsapp-${key}`}
                type={key === "accessToken" ? "password" : "text"}
                autoComplete="off"
                value={settings[key] || ""}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  change(key, event.target.value)
                }
              />
              <small className="form-text text-muted">{help}</small>
              <FieldErrors errors={getFieldErrors(key, errors) as string[]} />
            </CCol>
          </CRow>
        </CFormGroup>
      ))}
      <CFormGroup>
        <CRow>
          <CCol md={4}>
            <CLabel>Habilitar envíos</CLabel>
          </CCol>
          <CCol>
            <AnimatedCheckbox
              value={settings.sendEnable}
              onChange={(value) => change("sendEnable", value)}
            />
            <FieldErrors
              errors={getFieldErrors("sendEnable", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={4}>
            <CLabel>Enviar al teléfono del cliente</CLabel>
          </CCol>
          <CCol>
            <AnimatedCheckbox
              value={settings.sendToClient}
              onChange={(value) => change("sendToClient", value)}
            />
            <small className="form-text text-muted">
              Use teléfonos con código de país y clientes que hayan aceptado
              recibir notificaciones por WhatsApp.
            </small>
            <FieldErrors
              errors={getFieldErrors("sendToClient", errors) as string[]}
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow>
          <CCol md={4}>
            <CLabel htmlFor="whatsapp-recipients">
              Destinatarios adicionales
            </CLabel>
          </CCol>
          <CCol>
            <CInput
              id="whatsapp-recipients"
              value={recipients}
              placeholder="+595981123456, +595982123456"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setRecipients(event.target.value);
                setDirty(true);
              }}
            />
            <small className="form-text text-muted">
              Separados por comas. Reciben todos los avisos, incluidas las
              inutilizaciones, que no tienen cliente asociado.
            </small>
            <FieldErrors
              errors={
                getFieldErrors("additionalRecipients", errors) as string[]
              }
            />
          </CCol>
        </CRow>
      </CFormGroup>
      <div className="table-responsive">
        <table className="table table-sm">
          <caption>Plantillas de WhatsApp</caption>
          <thead>
            <tr>
              <th>Documento</th>
              <th>Plantilla</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(labels).map(([kind, label]) => {
              const name = `smartdoc_${kind}_${kind === "cancellation" || kind === "disablement" ? "v1" : "v2"}`;
              const template = settings.templates.find(
                (item) => item.kind === kind && item.name === name,
              );
              return (
                <tr key={kind}>
                  <td>{label}</td>
                  <td>
                    <code>{name}</code>
                  </td>
                  <td>
                    {statuses[template?.status || "NOT_CREATED"] ||
                      template?.status}
                    {template?.error && (
                      <div className="text-danger">{template.error}</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-muted">
        Los comprobantes se envían con el PDF adjunto. Las cancelaciones e
        inutilizaciones incluyen el motivo.
      </p>
      {settings.lastSyncedAt && (
        <p className="text-muted">
          Última sincronización:{" "}
          {new Date(settings.lastSyncedAt).toLocaleString()}
        </p>
      )}
      <CButtonGroup className="float-right mb-3">
        <CButton
          color="info"
          disabled={!saved || dirty}
          onClick={() => submit(true)}
        >
          Actualizar estados / reintentar
        </CButton>
        <CButton color="primary" onClick={() => submit(false)}>
          {submitting ? "Sincronizando..." : "Guardar y registrar plantillas"}
        </CButton>
      </CButtonGroup>
    </fieldset>
  );
};

export default TaxPayerWhatsappSettingsForm;
